import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { GlsOrg, WORKSPACE_TAG } from '@wetags/gls';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useLocationGroupStore } from '@frontend/scope';

/**
 * SINGLE SOURCE OF TRUTH for workspace feature availability.
 *
 * All workspace-related code MUST use this hook to determine
 * if workspace features are available and if workspace mode is valid.
 *
 *
 * @returns {Object} Workspace feature state
 * @property {boolean} isEnabled - Wetag is enabled (feature available)
 * @property {boolean} isLoading - Wetag is still loading
 * @property {boolean} isWorkspaceMode - Currently in valid workspace mode (wetag enabled + workspace scope)
 * @property {boolean} isInWorkspaceScope - User in workspace scope (may be invalid if wetag disabled!)
 * @property {boolean} shouldClearWorkspace - Invalid state detected (in workspace scope but wetag disabled)
 * @property {boolean} isWorkspaceAdmin - User has WORKSPACES_MANAGE permission
 *
 * @example
 * // For UI display
 * const { isEnabled, isLoading } = useWorkspaceFeature();
 * if (isLoading) return <Spinner />;
 * if (!isEnabled) return null;
 *
 * @example
 * // For logic validation
 * const { isWorkspaceMode } = useWorkspaceFeature();
 * const shouldApplyPolicy = isWorkspaceMode && someCondition;
 */
export const useWorkspaceFeature = () => {
  const [isEnabled, isLoading] = GlsOrg.useWetag(WORKSPACE_TAG);

  // Subscribe to a derived boolean — avoids re-renders during scope
  // initialization (undefined → location) since both map to `false`.
  const isInWorkspaceScope = useLocationGroupStore((state) => state.selectedScope?.scopeType === 'workspace');

  const isWorkspaceAdmin = useLocationGroupStore((state) =>
    state.accessibleLocationIds.some((id) => hasSchemaACL(id, Permission.WORKSPACES_MANAGE))
  );

  // Workspace mode is ONLY valid if:
  // 1. User is in workspace scope AND
  // 2. Wetag is enabled
  const isWorkspaceMode = isInWorkspaceScope && !!isEnabled;

  // Invalid state: user in workspace scope but wetag is disabled (and done loading)
  const shouldClearWorkspace = isInWorkspaceScope && !isEnabled && !isLoading;

  // All deps are booleans — the object reference only changes when a
  // boolean actually flips, keeping downstream consumers stable.
  return useMemo(
    () => ({
      // Feature availability
      isEnabled: !!isEnabled, // Wetag enabled (show UI)
      isLoading: !!isLoading, // Wetag loading

      // Workspace mode validation
      isWorkspaceMode, // Valid workspace mode (use for logic)

      // State flags
      isInWorkspaceScope, // User in workspace scope (may be invalid!)
      shouldClearWorkspace, // Invalid state (only after loading completes)

      // Permission checks
      isWorkspaceAdmin, // User has WORKSPACES_MANAGE ACL
    }),
    [isEnabled, isLoading, isWorkspaceMode, isInWorkspaceScope, shouldClearWorkspace, isWorkspaceAdmin]
  );
};
